import { html, TemplateResult } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import {
  DeviceControlExclusionRuleForm,
  DeviceControlExclusionRuleItem,
  DeviceControlExclusionsRuleType,
  DiscoveredDeviceItem,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { InputWidthValues } from '../../../../../../../../ui/gzInput';
import {
  getDeviceControlExceptionTypes,
  getDeviceControlPermissions,
} from '../../../../../utils/uiSettings/deviceControl/device-control.values';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { WEB_COMPONENT_DISCOVERED_DEVICES_ADD_EXCEPTION_CONTAINER } from '../../../../../../../../webcomponents.constants';
import { DiscoveredDevicesGridWebComponent } from '../gridComponents/discoveredDevicesGridWebComponent';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { WebComponentEvent } from '../../../../../../../../framework/BaseWebComponentController';
import _ from 'lodash';
import { styles } from '../view/styles';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';

@customElement(WEB_COMPONENT_DISCOVERED_DEVICES_ADD_EXCEPTION_CONTAINER)
export class DiscoveredDevicesAddExceptionContainerWebComponent extends GzPolicyFullPageWindowFormWebComponent<DeviceControlExclusionRuleForm> {
  private allItems: DeviceControlExclusionRuleItem[];
  private exclusionRuleForm: DeviceControlExclusionRuleForm;

  @query('#type-form-field')
  type: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#discovered-devices-form-field')
  exclusions: GzFormFieldWebComponent<DiscoveredDevicesGridWebComponent>;
  @query('#discovered-devices-grid-web-component')
  discoveredDevicesGridWebComponent: DiscoveredDevicesGridWebComponent;
  @query('#description-form-field')
  description: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#permission-form-field')
  permission: GzFormFieldWebComponent<GzDropdownWebComponent>;
  constructor(allItems: DeviceControlExclusionRuleItem[], exclusionRuleForm: DeviceControlExclusionRuleForm) {
    super();
    this.allItems = allItems;
    this.exclusionRuleForm = exclusionRuleForm;
  }

  static get styles() {
    return styles;
  }

  protected getConvertedFormValue() {
    const formValue = this.formGroup.value;

    const exclusions: DiscoveredDeviceItem[] = _.get(formValue, 'exclusions', []) as DiscoveredDeviceItem[];

    return exclusions.map(item => {
      const val = {
        ruleType: formValue.type,
        exception:
          formValue.type === DeviceControlExclusionsRuleType.DEVICE ? item.deviceId : item.productId.toString(),
        ruleName: formValue.description,
        permission: formValue.permission,
        userSID: '',
      };
      return val;
    });
  }
  private createFormValidators(): void {
    this.exclusions.touched = true;
    this.exclusions.Validators = [
      RequiredValidator(translateFn('Select at least one item.')),
      CustomizableValidator(this.validateDuplicates.bind(this)),
    ];
    this.type.Validators = [RequiredValidator(translateFn('Required field'))];
    this.description.Validators = [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(
        80,
        translateFn('The description must have a maximum of {maxLength} characters.', {
          maxLength: 80,
        })
      ),
    ];
    this.permission.Validators = [RequiredValidator(translateFn('Required field'))];
  }

  protected createFormGroup(): void {
    this.createFormValidators();
    this.formGroup = new FormGroup({
      type: this.type,
      exclusions: this.exclusions,
      description: this.description,
      permission: this.permission,
    });
  }

  setFormGroupValue(): void {
    this.formGroup.value = {
      type: this.exclusionRuleForm.type,
      exclusions: this.exclusionRuleForm.exclusions,
      description: this.exclusionRuleForm.description,
      permission: this.exclusionRuleForm.permission,
    };
  }

  protected renderContent(): TemplateResult {
    return html`<div style="padding-top: calc(var(--spacer) * 2)">
      <gz-form-field id="type-form-field" class="label-mw-250 mt-2" label="${translateFn('Exclusion type')}" required>
        <gz-dropdown
          id="type-dropdown"
          gz-form-input
          labelWidth="${InputWidthValues.LARGE}"
          .options="${getDeviceControlExceptionTypes()}"
          @selectionChanged="${this.onDropDownChange}"
        >
        </gz-dropdown>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id="discovered-devices-form-field">
        <gz-title fontSize="11" fontWeight="600" style="color: var(--chart-300-icon-buttons);"
          >${translateFn('EXCLUSIONS')}</gz-title
        >
        <gz-device-control-discovered-devices-grid-web-component
          id="discovered-devices-grid-web-component"
          gz-form-input
        ></gz-device-control-discovered-devices-grid-web-component>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field class="label-mw-250" id="description-form-field" label="${translateFn('Description')}" required>
        <gz-input
          id="description-input"
          placeholder="${translateFn('Add description.')}"
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id="permission-form-field" class="label-mw-250" label="${translateFn('Permission')}" required>
        <gz-dropdown
          id="permission-dropdown"
          gz-form-input
          labelWidth="${InputWidthValues.LARGE}"
          .options="${getDeviceControlPermissions()}"
        >
          <gz-input-error></gz-input-error>
        </gz-dropdown>
      </gz-form-field>
    </div>`;
  }

  public async onDropDownChange(event: WebComponentEvent) {
    const value = _.cloneDeep(this.exclusionRuleForm);
    value.type = event.detail.value;
    this.exclusionRuleForm = value;
    this.discoveredDevicesGridWebComponent.onValuesChanged();
  }

  private async validateDuplicates(addedExclusions: DiscoveredDeviceItem[]) {
    const type = this.formGroup.value.type;
    const exceptions = this.allItems.filter(item => item.ruleType === type).map(item => item.exception);
    const items =
      type === DeviceControlExclusionsRuleType.DEVICE
        ? addedExclusions.map(item => item.deviceId)
        : addedExclusions.map(item => item.productId.toString());

    for (const exclusion of items) {
      if (exceptions.includes(exclusion)) {
        return {
          valid: false,
          error: translateFn('An exclusion rule already exists for this item: {exclusionItem}', {
            exclusionItem: exclusion,
          }),
        };
      }
    }
    return {
      valid: true,
    };
  }
  public reset() {}
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_DISCOVERED_DEVICES_ADD_EXCEPTION_CONTAINER: DiscoveredDevicesAddExceptionContainerWebComponent;
  }
}
